/* eslint-disable  react/no-multi-comp */
/*  eslint-disable react/display-name */
/*  eslint-disable react/prop-types */

import React from 'react';
import { Dropdown, Space, Checkbox, Tag } from '@app/ui';

import TextFilter from '@app/components/common/TableFilters/TextFilter';
import { MultiSelectFilter } from '@app/components/common/TableFilters/MultiSelectFilter';
import { ParametersCollapse } from '@app/collections/components/ParametersCollapse/ParametersCollapse';

import * as format from '@app/utils/formatters';
import shortenStr from '@app/utils/shortenStr';
import {
    dateSorter,
    numberSorter,
    stringSorter,
} from '@app/utils/tableSorters';
import {
    documentToSeed,
    isVersion2Document,
} from '@app/components/automated-param-modal/automatedParametersDocument';

const METHODOLOGY_LEAGUE = 'league';

// The Parameters column shows one entry per parameter type. For a v2 document,
// unfold the nodes tree back to flat conditions and dedupe repeated types
// (v2 allows several rules on the same parameter).
export const getDisplayParameters = (record: any): { name: string }[] => {
    const { automatedParameters, automatedParametersDocument } = record ?? {};
    if (isVersion2Document(automatedParametersDocument)) {
        const names = documentToSeed(
            automatedParametersDocument,
        ).conditions.map(({ name }) => name);
        return [...new Set(names)].map(name => ({ name }));
    }
    return automatedParameters;
};

export const getCollectionsTableColumns = ({
    t,
    isSelected,
    onSelectFn,
    onEditFn,
    hasAutomatedCollections,
    multiSelectOptions,
    methodologyMultiSelectOptions,
    onSelectAllFn,
    isAllSelected,
    isIndeterminate,
    showMethodologyColumn,
}) => {
    let headers = [
        {
            id: '',
            title: (
                <Checkbox
                    data-testid="select-all-collections"
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={e => onSelectAllFn(e.target.checked)}
                />
            ),
            dataIndex: 'isSelected',
            render: (_: any, record: any) => {
                const _isSelected = isSelected(record.id);
                const _onSelectFn = onSelectFn(record.id);
                return (
                    <Checkbox
                        data-testid="checkbox-collection-list"
                        checked={_isSelected}
                        onChange={e => _onSelectFn(e.target.checked)}
                    />
                );
            },
            maxWidth: 50,
            filterable: false,
            sortable: false,
        },
        {
            title: t('tools:collections.name'),
            dataIndex: 'name',
            render: (value: any) => {
                return <div>{value}</div>;
            },
            sorter: (a: any, b: any) => stringSorter(a, b)('name'),
            filterInfo: {
                dataIndex: 'name',
                id: 'name',
                type: 'text',
                Filter: TextFilter,
                placeholder: t('tools:collections.enterName'),
            },
        },
        {
            title: t('tools:collections.description'),
            dataIndex: 'description',
            render: (value: any) => {
                return <div>{shortenStr(60, value || '')}</div>;
            },
            sorter: (a: any, b: any) => stringSorter(a, b)('description'),
            width: 250,
            filterInfo: {
                dataIndex: 'description',
                id: 'description',
                type: 'text',
                Filter: TextFilter,
                placeholder: t('tools:collections.enterDescription'),
            },
        },
        {
            title: t('tools:collections.createdDate'),
            dataIndex: 'addedAt',
            render: (value: any) => {
                return <div>{format.shortDate(value)}</div>;
            },
            format: format.shortDate,
            sorter: (a: any, b: any) => dateSorter(a, b)('addedAt'),
            defaultSortOrder: 'ascend',
            maxWidth: 100,
            filterable: false,
        },
        {
            title: t('tools:collections.createdBy'),
            dataIndex: 'creatorName',
            id: 'creatorName',
            render: (value: any) => {
                if (!value) return '';
                return <div>{shortenStr(30, value)}</div>;
            },
            filterInfo: {
                dataIndex: 'creatorName',
                id: 'creatorName',
                type: 'select',
                placeholder: t('tools:collections.select'),
                Filter: ({ onChange }) => (
                    <MultiSelectFilter
                        onChange={onChange}
                        multiSelectOptions={multiSelectOptions}
                    />
                ),
            },
            sorter: (a: any, b: any) => stringSorter(a, b)('creatorName'),
            width: 220,
            hasOverflow: true,
        },
        {
            title: t('tools:collections.methodology.title'),
            dataIndex: 'methodology',
            id: 'methodology',
            render: (value: string) => {
                const isLeague = value === METHODOLOGY_LEAGUE;
                return (
                    <Tag color={isLeague ? 'blue' : 'default'}>
                        {isLeague
                            ? t('tools:collections.methodology.league')
                            : t('tools:collections.methodology.team')}
                    </Tag>
                );
            },
            filterInfo: {
                dataIndex: 'methodology',
                id: 'methodology',
                type: 'select',
                placeholder: t('tools:collections.select'),
                Filter: ({ onChange }) => (
                    <MultiSelectFilter
                        onChange={onChange}
                        multiSelectOptions={methodologyMultiSelectOptions}
                    />
                ),
            },
            sorter: (a: any, b: any) => stringSorter(a, b)('methodology'),
            width: 180,
        },
        {
            title: t('tools:collections.parameters'),
            id: 'automatedParameters',
            dataIndex: 'automatedParameters',
            render: (_: any, record: any) => {
                return (
                    <ParametersCollapse
                        parameters={getDisplayParameters(record)}
                    />
                );
            },
            filterable: false,
        },
        {
            title: t('tools:collections.postCount'),
            dataIndex: 'postCount',
            sorter: (a: any, b: any) => numberSorter(a, b)('postCount'),
            maxWidth: 100,
            filterable: false,
        },
        {
            title: '',
            dataIndex: 'id',
            width: 50,
            minWidth: 50,
            fixed: 'right',
            filterable: false,
            style: {
                overflow: 'visible',
                padding: 0,
            },
            render: (_: any, record: any) => {
                const { id } = record;

                const _onEditFn = onEditFn(id);

                const items = [
                    {
                        key: 'edit',
                        disabled: false,
                        label: (
                            <div
                                onClick={_onEditFn}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                }}
                            >
                                <i className="fa fa-pencil" />
                                {t('tools:collections.actions.edit')}
                            </div>
                        ),
                    },
                ];

                return (
                    <Space
                        size="middle"
                        style={{
                            display: 'block',
                            height: '100%',
                            width: '100%',
                        }}
                    >
                        <Dropdown
                            trigger={['click']}
                            menu={{
                                items,
                            }}
                            rootClassName="collections-table-actions"
                        >
                            <a
                                style={{
                                    display: 'block',
                                    height: '100%',
                                    width: '100%',
                                }}
                                data-test="collection-actions-dropdown"
                            >
                                <i
                                    className="fa-solid fa-ellipsis-vertical"
                                    style={{ fontSize: 16, color: '#009FDF' }}
                                />
                            </a>
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];
    if (!hasAutomatedCollections) {
        headers = headers.filter(c => c.id !== 'automatedParameters');
    }
    if (!showMethodologyColumn) {
        headers = headers.filter(c => c.id !== 'methodology');
    }

    return headers;
};
